import { useNavigate } from "react-router-dom";
import Button from "../shared/components/buttons";
import { FC, useCallback, useEffect, useRef } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { PageProps } from "shared/defines";
import { marketingPermissionState } from "../state/parent-details";
import { TextDecoder } from "../shared/text-effects/TextDecoder";
import { useTextSize } from "../shared/hooks/useTextSize";
import { submitWaiver } from "../waiverService";
import { useResetStoreExceptEmail } from "../state/utils";
import { storeState } from "../state/storeState";

const ConfirmPermissionPage: FC<PageProps> = ({ title }) => {
  const btnRef = useRef<HTMLButtonElement>(null);
  const hiddenRef = useRef<HTMLHeadingElement>(null);
  const { height: titleHeight } = useTextSize(hiddenRef);

  const navigate = useNavigate();
  const setMarketingPermission = useSetRecoilState(marketingPermissionState);
  const resetStore = useResetStoreExceptEmail();
  const values = useRecoilValue(storeState);

  const handleEnter = useCallback((event: KeyboardEvent) => {
    if (event.key === "Enter") {
      btnRef.current?.click();
    }
  }, []);

  useEffect(() => {
    window.addEventListener("keydown", handleEnter);

    return () => {
      window.removeEventListener("keydown", handleEnter);
    };
  }, [handleEnter]);

  const onContinueButtonClick = () => {
    setMarketingPermission(true);
    submitForm(true);
  };

  const onDeclineButtonClick = () => {
    setMarketingPermission(false);
    submitForm(false);
  };

  const submitForm = (marketingConsent: boolean) => {
    submitWaiver({ ...values, marketingConsent })
      .then(() => {
        resetStore();
        navigate("/complete");
      })
      .catch((e) => {
        console.error(e);
        navigate("/invalid");
      });
  };

  useEffect(() => {
    document.title = title;
  }, [title]);

  return (
    <div className="w-full flex-1 items-start sm:items-center flex">
      <div className="flex flex-col w-full">
        <div
          className="whitespace-normal w-full relative mb-10"
          style={{ height: `${titleHeight}px` }}
        >
          <TextDecoder
            className="font-aeonik font-medium text-[2.125rem]/[2.125rem] sm:text-[3.1rem]/[3.4rem] gap-5"
            delay={0}
            duration={500}
            animationType={"in"}
            text="Confirm permission"
          />
          <div
            ref={hiddenRef}
            className="absolute opacity-0 top-0 pointer-events-none font-aeonik font-medium text-[2.125rem]/[2.125rem] sm:text-[3.1rem]/[3.4rem]"
          >
            Confirm permission
          </div>
        </div>

        <div className="text-[1.2rem]/[1.2rem] font-medium border-b-1 border-white pb-3">
          To receive player scores and rewards by email
        </div>

        <Button
          ref={btnRef}
          variant="filled"
          size="sm"
          className="mt-5 text-[1.2rem] w-full sm:w-57.5"
          onClick={onContinueButtonClick}
        >
          Confirm
        </Button>
        <Button
          variant="outlined"
          size="sm"
          className="mt-2.5 text-[1.2rem] w-full sm:w-57.5"
          onClick={onDeclineButtonClick}
        >
          Decline
        </Button>
      </div>
    </div>
  );
};

export default ConfirmPermissionPage;
